import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { assign } from 'lodash';
import { AlertMessageService } from 'app/core/common/alert.message.service';
import { ReportsService } from 'app/modules/admin/transactions/inwards/reports.service';
import { BuiltUpAreaLandService } from '../builtup-area-land.service';
import { CommonDropDown, SingleCommonDropDown } from 'app/core/common/types/dropdown.types';
import { LookupService } from 'app/core/common/services/lookup.service';
import { BuiltUpAreaLand } from '../builtUpAreaLand.types';

@Component({
    selector: 'builtup-area-land-details',
    templateUrl: './builtup-area-land-details.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuiltUpAreaLandDetailsComponent implements OnInit, OnDestroy {

    builtUpAreaLand: BuiltUpAreaLand;
    builtUpAreaLandForm: FormGroup;
    dialogTitle = `New Land Area`
    alert: any;
    reportType: string;
    total;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    options: CommonDropDown[];  
    filteredOptions: Observable<CommonDropDown[]>;
   

    builtUpAreaLandFilterForm: FormGroup;
    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) private _data: {
            builtUpAreaLand: BuiltUpAreaLand,
            reportType: string
        },
        private builtUpAreaLandService: BuiltUpAreaLandService,
        private _matDialogRef: MatDialogRef<BuiltUpAreaLandDetailsComponent>,
        private _formBuilder: FormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        private _alertMessage: AlertMessageService,
        private _lookupService: LookupService
    ) {
        this.reportType = this._data.reportType;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Open the drawer
        this.builtUpAreaLandForm = this._formBuilder.group({
            id: [''],
            reportId: [this._data.builtUpAreaLand.reportId],
            type: [this._data.builtUpAreaLand.type, [Validators.required]],
           // type: ['', [Validators.required]],
            area: [this._data.builtUpAreaLand.area],
            areaAsPer: [''],
            orderIndex: [this._data.builtUpAreaLand.orderIndex],
        });

        this._lookupService.getOptionByTypeId(81, this.reportType).subscribe((res) => {
            this.options = res;
            this.filteredOptions = this.builtUpAreaLandForm.get("areaAsPer")
                .valueChanges.pipe(
                    startWith(''),
                    map(value => this._optionsfilter(value || '')),
                );
        })

        if (this._data.builtUpAreaLand.id) {
            // Request the data from the server
            this.builtUpAreaLandService.getBuiltUpAreaLand(this._data.builtUpAreaLand.id)
                .subscribe((res) => {
                    if (res.isSuccess) {
                        this.builtUpAreaLand = res.result;
                        this.dialogTitle = `Edit Land Area`;
                        this.builtUpAreaLandForm.patchValue(this.builtUpAreaLand);
                    }
                    else {
                        this._alertMessage.error(res.errors.join('\n'));
                        this.onClose();
                    }
                });
        }
}

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Close the drawer
     */
    // closeDrawer(): Promise<MatDrawerToggleResult> {
    //     return this._optionTypesListComponent.matDrawer.close();
    // }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
    setResetForm(oldData: BuiltUpAreaLand) {
        this._data.builtUpAreaLand as BuiltUpAreaLand;
        this._data.builtUpAreaLand.reportId = oldData.reportId;
        this._data.builtUpAreaLand.orderIndex = oldData.orderIndex + 1;
        this.builtUpAreaLandForm.reset();
        this.builtUpAreaLandForm.patchValue(this._data.builtUpAreaLand);
    }
    saveAndNew() {
        const builtUpAreaLand = this.builtUpAreaLandForm.getRawValue();
        this.save(builtUpAreaLand, true)
    }
    saveAndClose() {
        const builtUpAreaLand = this.builtUpAreaLandForm.getRawValue();

        this.save(builtUpAreaLand);
    }

    save(builtUpAreaLand, isOpen = false) {
        this.builtUpAreaLandService.createBuiltUpAreaLand(builtUpAreaLand).subscribe((response) => {
            if (response.isSuccess) {
                // Show a success message
                this._alertMessage.success("BuiltUpAreaLand added successfully");
                // Close the dialog
                if (!isOpen) {
                    this._matDialogRef.close(response);
                }
                else {
                    this.setResetForm(builtUpAreaLand);
                }
            }
            else {
                this._alertMessage.error(response.errors.join("\n"));
            }
        });
    }
   
    updateAndClose() {

        const builtUpAreaLand = this.builtUpAreaLandForm.getRawValue();

        this.builtUpAreaLandService.updateBuiltUpAreaLand(builtUpAreaLand).subscribe((response) => {
            if (response.isSuccess) {
                // Show a success message
                this._alertMessage.success("BuiltUpAreaLand updated successfully");
                // Close the dialog
                this._matDialogRef.close(response);
            }
            else {
                this._alertMessage.error(response.errors.join("\n"));
            }
        });

    }

    onClose(): void {
        this._matDialogRef.close();
    }

    displayOptionsFn(options: CommonDropDown[]): (name: string) => string {
        return (name: string) => {
            const correspondingOption = Array.isArray(options) ? options.find(option => option.name === name) : null;
            return correspondingOption ? correspondingOption.name : '';
        }
    }

    private _optionsfilter(name: string): CommonDropDown[] {
        const filterValue = name.toString().toLowerCase();

        return this.options.filter(option => option.name.toLowerCase().includes(filterValue));
    }
}
